import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Upload, X, Crop, Check, GripVertical, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAdminAuth } from '../context/AdminAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageGalleryEditor = ({ images = [], onChange, maxImages = 4, label = "Gallery Images" }) => {
  const { getAuthHeader } = useAdminAuth();
  const [uploading, setUploading] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [cropImageIndex, setCropImageIndex] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 100, height: 100, x: 0, y: 0 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    const filesToUpload = files.slice(0, remainingSlots);

    setUploading(true);

    for (const file of filesToUpload) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API_URL}/api/upload/image`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        );

        const imageUrl = response.data.url.startsWith('http')
          ? response.data.url
          : `${API_URL}${response.data.url}`;

        onChange([...images, imageUrl]);
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    setUploading(false);
  };

  // Remove image
  const removeImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onChange(newImages);
  };

  // Open crop modal
  const openCropModal = (imageUrl, index) => {
    setCropImage(imageUrl);
    setCropImageIndex(index);
    setCrop({ unit: '%', width: 100, height: 100, x: 0, y: 0 });
    setCompletedCrop(null);
    setCropModalOpen(true);
  };

  // Apply crop
  const applyCrop = useCallback(async () => {
    if (!completedCrop || !imgRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const image = imgRef.current;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Convert to blob and upload
    canvas.toBlob(async (blob) => {
      if (!blob) return;

      setUploading(true);

      try {
        const formData = new FormData();
        formData.append('file', blob, 'cropped-image.jpg');

        const response = await axios.post(
          `${API_URL}/api/upload/image`,
          formData,
          { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
        );

        const imageUrl = response.data.url.startsWith('http')
          ? response.data.url
          : `${API_URL}${response.data.url}`;

        // Replace the image at the index
        const newImages = [...images];
        newImages[cropImageIndex] = imageUrl;
        onChange(newImages);

        setCropModalOpen(false);
        setCropImage(null);
        setCropImageIndex(null);
      } catch (error) {
        console.error('Upload error:', error);
      }

      setUploading(false);
    }, 'image/jpeg', 0.9);
  }, [completedCrop, images, cropImageIndex, onChange, getAuthHeader]);

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);
    onChange(newImages);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      {/* Image grid with drag and drop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {images.map((image, index) => (
          <div
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-move group ${
              draggedIndex === index ? 'border-purple-500 opacity-50' : 'border-gray-200 hover:border-purple-400'
            }`}
          >
            <img src={image} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
            
            {/* Drag handle */}
            <div className="absolute top-2 left-2 p-1 bg-white/80 rounded opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="w-4 h-4 text-gray-600" />
            </div>

            {/* Action buttons */}
            <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => openCropModal(image, index)}
                className="p-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg shadow-lg transition-colors"
                title="Crop image"
              >
                <Crop className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => removeImage(index)}
                className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-lg transition-colors"
                title="Remove image"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Image number */}
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-xs font-bold rounded">
              #{index + 1}
            </div>
          </div>
        ))}

        {/* Upload slot */}
        {images.length < maxImages && (
          <label className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-400 flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-purple-50 transition-colors">
            {uploading ? (
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                <span className="text-sm text-gray-500 mt-2">Uploading...</span>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Add Image</span>
                <span className="text-xs text-gray-400">{images.length}/{maxImages}</span>
              </>
            )}
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
          </label>
        )}
      </div>

      <p className="text-xs text-gray-500">
        Drag to reorder. Click crop icon to adjust image. Max {maxImages} images.
      </p>

      {/* Crop Modal */}
      {cropModalOpen && cropImage && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">Crop Image</h3>
              <button
                onClick={() => setCropModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 max-h-[60vh] overflow-auto flex items-center justify-center bg-gray-100">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={undefined}
              >
                <img
                  ref={imgRef}
                  src={cropImage}
                  alt="Crop preview"
                  style={{ maxHeight: '50vh', maxWidth: '100%' }}
                  crossOrigin="anonymous"
                />
              </ReactCrop>
            </div>

            <div className="p-4 border-t flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Drag on the image to select crop area
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setCropModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={applyCrop}
                  disabled={!completedCrop || uploading}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg flex items-center space-x-2 transition-colors"
                >
                  {uploading ? (
                    <span>Saving...</span>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Apply Crop</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGalleryEditor;
